import { UTCDate } from '@date-fns/utc';
import { endOfDay, startOfDay, subMonths, subYears } from 'date-fns';
import { useMemo, useState } from 'react';
import Chart from 'react-google-charts';
import { Button, Checkbox, Dropdown, Icon, Label } from 'semantic-ui-react';
import Loader from '../../../../../components/loader';
import AuthService from '../../../../../service/auth';
import QuantityPerformanceConfig from '../productivity/components/quantityPerformanceConfig';
import {
    All_PROPS,
    userIDsWithKPIsChartReadPermissions,
    DEFAULT_CART_CFG_OPS,
    INTERVAL_TYPE,
    StatisticProps,
    kpisIgnoredUsers,
} from './constants';
import './styles.css';
import { createStatisticsArray, getIntervals, useActivity, useProductivity, getChartData } from './utils';
import useUsersMap from '../../../../../hooks/useUsersMap';
import { ROLES } from '../../../../../constnats/user';
import { getTargetUsersMap, getUserOptions } from './userDeclaredWorkUtils';

const END_OF_TODAY = endOfDay(new UTCDate());

const INTERVAL_DEFAULT_SPEC = {
    [INTERVAL_TYPE.WEEKLY]: {
        startDateUTC: startOfDay(subMonths(END_OF_TODAY, 1)),
        endDateUTC: END_OF_TODAY,
        intervalType: INTERVAL_TYPE.WEEKLY,
    },
    [INTERVAL_TYPE.MONTHLY]: {
        startDateUTC: startOfDay(subYears(END_OF_TODAY, 1)),
        endDateUTC: END_OF_TODAY,
        intervalType: INTERVAL_TYPE.MONTHLY,
    },
};

function getIntervalOption(frameInMonths) {
    return {
        label: 'Past ' + frameInMonths + ' months',
        getIntervalSpec: (endDateUTC) => {
            return {
                startDateUTC: startOfDay(subMonths(endDateUTC, frameInMonths)),
            };
        },
    };
}

const INTERVAL_TIME_FRAME_OPTIONS = {
    [INTERVAL_TYPE.WEEKLY]: [getIntervalOption(1), getIntervalOption(6), getIntervalOption(13)],
    [INTERVAL_TYPE.MONTHLY]: [getIntervalOption(13), getIntervalOption(25)],
};

function KpisChart() {
    const { usersMap, loading: loadingUsers } = useUsersMap({ includeDisabled: true, userRoles: [ROLES.EMPLOYEE] });
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [intervalSpec, setIntervalSpec] = useState({
        ...INTERVAL_DEFAULT_SPEC[INTERVAL_TYPE.WEEKLY],
    });

    const intervals = useMemo(
        () => getIntervals(intervalSpec.startDateUTC, intervalSpec.endDateUTC, intervalSpec.intervalType),
        [intervalSpec]
    );

    /**
     * Contains the users for which the statistics needs to be calculated.
     * Excluding specific users listed in @see kpisIgnoredUsers
     */
    const eligibleUsersMap = useMemo(() => Object.values(usersMap)
        .filter(user => !kpisIgnoredUsers.includes(Number(user.id)))
        .reduce((acc,curr) => {
            acc[curr.id] = curr;
            return acc
        },{}),[usersMap])

    const userOptions = useMemo(() => getUserOptions(eligibleUsersMap), [eligibleUsersMap]);
    const targetUsersMap = useMemo(() => getTargetUsersMap(eligibleUsersMap, selectedUserId),
        [eligibleUsersMap, selectedUserId]);

    const [productivity, loadingProductivity, allTableTypes, config, setConfig] = useProductivity(targetUsersMap, intervals);
    const [activity, loadingActivity] = useActivity(intervals, selectedUserId);
    const [propsToPlotOnChart, setPropsToPlotOnChart] = useState([
        StatisticProps.totalScoreToActualWorkingHoursRatio.propName,
    ]);
    const loading = loadingUsers || loadingProductivity || loadingActivity;
    const combinedStats = useMemo(() => loading ? [] : createStatisticsArray(productivity, activity, selectedUserId, intervalSpec.intervalType),
        [loading, productivity, activity, selectedUserId, intervalSpec.intervalType]);
    const availableProps = All_PROPS.filter(prop => combinedStats.some(row => Number.isFinite(row.total[prop.propName])))
        .map(prop => prop.propName);

    const handlePropToPlotOnChartToggle = (prop) => {
        setPropsToPlotOnChart((prev) => {
            if (prev.includes(prop)) {
                return prev.filter((x) => x !== prop);
            } else {
                return [...prev, prop];
            }
        });
    };

    const handleIntervalSpecChange = () => {
        setIntervalSpec((prev) => {
            const newMode = prev.intervalType === INTERVAL_TYPE.WEEKLY ? INTERVAL_TYPE.MONTHLY : INTERVAL_TYPE.WEEKLY;
            let newIntervalSpec = { ...prev };
            if (prev.intervalType !== newMode) {
                newIntervalSpec = { ...INTERVAL_DEFAULT_SPEC[newMode] };
            }
            return newIntervalSpec;
        });
    };

    if (!userIDsWithKPIsChartReadPermissions.includes(AuthService.getUserId())) {
        return <>Not enough privileges!</>;
    }

    return (
        <>
            <QuantityPerformanceConfig config={config} tableTypes={allTableTypes} onChange={setConfig} />
            <div className="kpis-chart__controls">
                <div className="kpis-chart__user-filter">
                    <label id="kpis-chart-user-label">User</label>
                    <Dropdown
                        aria-labelledby="kpis-chart-user-label"
                        selection search
                        loading={loadingUsers}
                        disabled={loadingUsers}
                        options={userOptions}
                        value={selectedUserId ?? 'ALL'}
                        onChange={(_, { value }) => setSelectedUserId(value === 'ALL' ? null : Number(value))}
                    />
                </div>
                <div className="kpis-chart__mode-switch">
                    <Label>Monthly</Label>
                    <Checkbox
                        onChange={handleIntervalSpecChange}
                        checked={intervalSpec.intervalType === INTERVAL_TYPE.WEEKLY}
                        slider
                    />
                    <Label>Weekly</Label>
                </div>
            </div>
            <div className="kpis-chart__chart-container">
                <table className="kpis-chart__props-table">
                    <tbody>
                        {All_PROPS.map((prop) => {
                            const isPropIncluded = propsToPlotOnChart.includes(prop.propName);
                            const iconName = isPropIncluded ? 'checkmark' : 'x';
                            const iconColor = isPropIncluded ? 'green' : 'red';
                            return (
                                <tr
                                    key={prop.propName}
                                    onClick={() => handlePropToPlotOnChartToggle(prop.propName)}
                                    className="cursorPointer"
                                >
                                    <td>{<Icon size="tiny" name={iconName} color={iconColor} />}</td>
                                    <td>
                                        {prop.label}
                                        {!loading && !availableProps.includes(prop.propName) && (
                                            <span className="kpis-chart__unavailable">No data available</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <div className="kpi-chart__chart">
                    {loading ? (
                        <Loader />
                    ) : (
                        <ChartComponent
                            combinedStats={combinedStats}
                            availableProps={availableProps}
                            propsToPlotOnChart={propsToPlotOnChart}
                        />
                    )}
                    <IntervalOptions
                        setIntervalSpec={setIntervalSpec}
                        options={INTERVAL_TIME_FRAME_OPTIONS[intervalSpec.intervalType]}
                    />
                </div>
            </div>
        </>
    );
}

function ChartComponent({ combinedStats, availableProps, propsToPlotOnChart }) {
    const plottedProps = propsToPlotOnChart.filter(prop => availableProps.includes(prop));
    const unavailableProps = propsToPlotOnChart.filter(prop => !availableProps.includes(prop));
    if (propsToPlotOnChart.length === 0 || plottedProps.length === 0) {
        return <div className="kpis-chart__empty" role="status">
            {propsToPlotOnChart.length === 0 ? 'Select a metric to display.' : 'No data available for the selected metrics.'}
        </div>;
    }
    const chartData = getChartData(combinedStats, plottedProps);
    return <>
        {unavailableProps.length > 0 && <p role="status">
            No data available: {unavailableProps.map(prop => StatisticProps[prop].label).join(', ')}.
        </p>}
        <Chart chartType="LineChart" width="100%" height="450px" data={chartData} options={DEFAULT_CART_CFG_OPS} />
    </>;
}

function IntervalOptions({ options, setIntervalSpec }) {
    const handleClick = (option) => {
        setIntervalSpec((prev) => {
            const { startDateUTC } = option.getIntervalSpec(prev.endDateUTC);
            return {
                ...prev,
                startDateUTC,
            };
        });
    };

    return (
        <div className="kpis-chart__interval-options">
            {options.map((option) => (
                <Button key={option.label} onClick={() => handleClick(option)}>
                    {option.label}
                </Button>
            ))}
        </div>
    );
}

export default KpisChart;
